import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, QueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, DollarSign, Building2, Crown, X, Loader2, Download, Clock, Plus } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { ErrorBannerWithId } from "@/components/error-id-chip";
import TotpManagementSection from "@/components/TotpManagementSection";
import { ToastFn, VendorProfile, VendorSession } from "./_shared";
import { MarketplaceSubState } from "./MarketplaceTab";



// ── Profile Tab ───────────────────────────────────────────────────────────────

export function ProfileTab({ vendor, vendorLoading, vendorId, session, queryClient, toast }: {
  vendor?: VendorProfile;
  vendorLoading: boolean;
  vendorId: number;
  session: VendorSession;
  queryClient: QueryClient;
  toast: ToastFn;
}) {
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Personal contact (editable by all team members for self)
  const [personalFirstName, setPersonalFirstName] = useState("");
  const [personalLastName, setPersonalLastName] = useState("");
  const [personalPhone, setPersonalPhone] = useState("");
  const [personalEmail, setPersonalEmail] = useState("");
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [personalError, setPersonalError] = useState("");

  // Change-phone dialog
  const [changePhoneOpen, setChangePhoneOpen] = useState(false);
  const [changePhoneStep, setChangePhoneStep] = useState<"send" | "confirm">("send");
  const [changePhoneSending, setChangePhoneSending] = useState(false);
  const [changePhoneCode, setChangePhoneCode] = useState("");
  const [changePhoneNewNumber, setChangePhoneNewNumber] = useState("");
  const [changePhoneConfirming, setChangePhoneConfirming] = useState(false);
  const [changePhoneError, setChangePhoneError] = useState("");

  // Change-email dialog
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [changeEmailContext, setChangeEmailContext] = useState<"personal" | "business">("personal");
  const [changeEmailStep, setChangeEmailStep] = useState<"send" | "confirm">("send");
  const [changeEmailSending, setChangeEmailSending] = useState(false);
  const [changeEmailCode, setChangeEmailCode] = useState("");
  const [changeEmailNewAddress, setChangeEmailNewAddress] = useState("");
  const [changeEmailConfirming, setChangeEmailConfirming] = useState(false);
  const [changeEmailError, setChangeEmailError] = useState("");

  // Notification preferences
  const [smsTaskAssigned, setSmsTaskAssigned] = useState(true);
  const [smsTaskMessage, setSmsTaskMessage] = useState(true);
  const [smsFormMessage, setSmsFormMessage] = useState(true);
  const [emailTaskAssigned, setEmailTaskAssigned] = useState(false);
  const [emailTaskMessage, setEmailTaskMessage] = useState(false);
  const [emailFormMessage, setEmailFormMessage] = useState(false);
  const [emailPayoutStatus, setEmailPayoutStatus] = useState(true);
  // Task #2103 — Vendor-wide in-app toggle for the `service_form_sent`
  // notification. Lives on the `vendors` row, so the server persists it
  // on the vendor record while still riding the existing
  // /api/vendor-portal/preferences PATCH.
  const [notifyInAppFormSent, setNotifyInAppFormSent] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Task #2187 — default rate settings (owner-only)
  const [defaultRateType, setDefaultRateType] = useState<"hourly" | "fixed">("hourly");
  const [defaultRateInput, setDefaultRateInput] = useState<string>("");
  const [defaultTravelFeeInput, setDefaultTravelFeeInput] = useState<string>("");
  const [savingRates, setSavingRates] = useState(false);
  // Flat-rate offerings (named services with fixed prices)
  const [flatOfferings, setFlatOfferings] = useState<Array<{ name: string; priceCents: number }>>([]);
  const [newOfferingName, setNewOfferingName] = useState("");
  const [newOfferingPrice, setNewOfferingPrice] = useState("");

  // Marketplace listing (owner-only). The actual "is this vendor visible
  // in the public marketplace?" flag is now Stripe-controlled via the
  // /marketplace-subscription endpoints — this component fetches the
  // server-derived state and renders Subscribe / Manage / Pause / Resume /
  // Cancel actions instead of a toggle.
  const [marketplaceBio, setMarketplaceBio] = useState("");
  const [marketplacePhotoUrl, setMarketplacePhotoUrl] = useState<string>("");
  const [savingMarketplace, setSavingMarketplace] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [marketplaceError, setMarketplaceError] = useState("");
  const [marketplaceActionPending, setMarketplaceActionPending] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>("all");
  const [exportingBulk, setExportingBulk] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const downloadFeeReceipt = useCallback(async (fee: { id: number; monthKey: string; chargedCents: number; createdAt: string; taskId: number | null; taskTitle: string | null }) => {
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryBlue: [number, number, number] = [0, 82, 204];
    const accentOrange: [number, number, number] = [255, 165, 0];

    const [year, month] = fee.monthKey.split("-");
    const monthLabel = new Date(Number(year), Number(month) - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
    const dateLabel = new Date(fee.createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const vendorDisplayName = vendor?.businessName || vendor?.name || "Vendor";
    const amount = `$${(fee.chargedCents / 100).toFixed(2)}`;

    doc.setFillColor(...primaryBlue);
    doc.rect(0, 0, pageWidth, 36, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("PadPulse", 14, 18);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Marketplace Fee Receipt", 14, 27);
    doc.text(dateLabel, pageWidth - 14, 18, { align: "right" });

    doc.setFillColor(...accentOrange);
    doc.rect(14, 44, pageWidth - 28, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Receipt Details", 17, 49.5);

    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const rows: [string, string][] = [
      ["Vendor", vendorDisplayName],
      ["Billing Period", monthLabel],
      ["Charge Date", dateLabel],
      ["Amount", amount],
    ];
    if (fee.taskId != null) {
      rows.push(["Task", fee.taskTitle ?? `Task #${fee.taskId}`]);
    }

    let y = 62;
    rows.forEach(([label, value], i) => {
      if (i % 2 === 0) {
        doc.setFillColor(245, 247, 250);
        doc.rect(14, y - 4, pageWidth - 28, 9, "F");
      }
      doc.setFont("helvetica", "bold");
      doc.text(label, 17, y + 1.5);
      doc.setFont("helvetica", "normal");
      doc.text(value, pageWidth - 17, y + 1.5, { align: "right" });
      y += 10;
    });

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text("PadPulse — padpulse.io", pageWidth / 2, 280, { align: "center" });

    doc.save(`padpulse-marketplace-fee-${fee.monthKey}-receipt.pdf`);
  }, [vendor]);

  const downloadBulkReceipts = useCallback(async (fees: Array<{ id: number; monthKey: string; chargedCents: number; createdAt: string; taskId: number | null; taskTitle: string | null }>, yearLabel: string) => {
    if (fees.length === 0) return;
    setExportingBulk(true);
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const primaryBlue: [number, number, number] = [0, 82, 204];
      const accentOrange: [number, number, number] = [255, 165, 0];
      const vendorDisplayName = vendor?.businessName || vendor?.name || "Vendor";
      const exportDate = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

      doc.setFillColor(...primaryBlue);
      doc.rect(0, 0, pageWidth, 36, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.text("PadPulse", 14, 18);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Marketplace Fee History Export", 14, 27);
      doc.text(exportDate, pageWidth - 14, 18, { align: "right" });

      doc.setFillColor(...accentOrange);
      doc.rect(14, 44, pageWidth - 28, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(`${yearLabel} — ${vendorDisplayName}`, 17, 49.5);

      const colMonth = 17;
      const colDate = 70;
      const colJob = 110;
      const colAmount = pageWidth - 17;

      let y = 66;
      doc.setFontSize(8.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100, 100, 100);
      doc.text("Billing Period", colMonth, y);
      doc.text("Charge Date", colDate, y);
      doc.text("Triggering Job", colJob, y);
      doc.text("Amount", colAmount, y, { align: "right" });
      y += 5;
      doc.setDrawColor(220, 220, 220);
      doc.line(14, y, pageWidth - 14, y);
      y += 5;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8.5);

      fees.forEach((fee, i) => {
        if (y > pageHeight - 25) {
          doc.addPage();
          y = 20;
        }
        const [yr, mo] = fee.monthKey.split("-");
        const monthLabel = new Date(Number(yr), Number(mo) - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
        const dateLabel = new Date(fee.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        const amount = `$${(fee.chargedCents / 100).toFixed(2)}`;
        const jobLabel = fee.taskTitle ? fee.taskTitle.slice(0, 28) + (fee.taskTitle.length > 28 ? "…" : "") : "—";

        if (i % 2 === 0) {
          doc.setFillColor(245, 247, 250);
          doc.rect(14, y - 4, pageWidth - 28, 9, "F");
        }
        doc.text(monthLabel, colMonth, y + 1.5);
        doc.text(dateLabel, colDate, y + 1.5);
        doc.text(jobLabel, colJob, y + 1.5);
        doc.text(amount, colAmount, y + 1.5, { align: "right" });
        y += 10;
      });

      y += 4;
      doc.setDrawColor(220, 220, 220);
      doc.line(14, y, pageWidth - 14, y);
      y += 8;
      const totalCents = fees.reduce((s, f) => s + f.chargedCents, 0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total (${fees.length} receipt${fees.length !== 1 ? "s" : ""})`, colMonth, y);
      doc.text(`$${(totalCents / 100).toFixed(2)}`, colAmount, y, { align: "right" });

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 150, 150);
      doc.text("PadPulse — padpulse.io", pageWidth / 2, pageHeight - 10, { align: "center" });

      const slug = yearLabel === "All Years" ? "all" : yearLabel;
      doc.save(`padpulse-marketplace-fees-${slug}-export.pdf`);
    } finally {
      setExportingBulk(false);
    }
  }, [vendor]);

  const { data: marketplaceSub, refetch: refetchMarketplaceSub } = useQuery<MarketplaceSubState>({
    queryKey: ["/api/vendor-portal/marketplace-subscription"],
    enabled: !!session.isOwner,
  });

  const { data: feeHistory = [], isLoading: feeHistoryLoading } = useQuery<Array<{
    id: number;
    monthKey: string;
    chargedCents: number;
    createdAt: string;
    taskId: number | null;
    taskTitle: string | null;
    paymentTransactionId: number | null;
  }>>({
    queryKey: ["/api/vendor-portal/marketplace-fee-history"],
    enabled: !!session.isOwner,
  });

  useEffect(() => {
    if (vendor) {
      setName(vendor.name || "");
      setBusinessName(vendor.businessName || "");
      setEmail(vendor.email || "");
      const v = vendor as VendorProfile & { personalFirstName?: string | null; personalLastName?: string | null; personalPhone?: string | null; personalEmail?: string | null };
      setPersonalFirstName(v.personalFirstName || "");
      setPersonalLastName(v.personalLastName || "");
      setPersonalPhone(v.personalPhone || "");
      setPersonalEmail(v.personalEmail || "");
      setSmsTaskAssigned(vendor.smsTaskAssigned ?? true);
      setSmsTaskMessage(vendor.smsTaskMessage ?? true);
      setSmsFormMessage(vendor.smsFormMessage ?? true);
      setEmailTaskAssigned(vendor.emailTaskAssigned ?? false);
      setEmailTaskMessage(vendor.emailTaskMessage ?? false);
      setEmailFormMessage(vendor.emailFormMessage ?? false);
      setEmailPayoutStatus(vendor.emailPayoutStatus ?? true);
      setNotifyInAppFormSent(vendor.notifyInAppFormSent ?? true);
      // Task #2187 — rate settings
      const vr = vendor as VendorProfile & { defaultRateCents?: number | null; rateType?: string | null; defaultTravelFeeCents?: number | null; flatRateOfferings?: Array<{ name: string; priceCents: number }> | null };
      if (vr.rateType === "fixed" || vr.rateType === "hourly") setDefaultRateType(vr.rateType);
      setDefaultRateInput(vr.defaultRateCents ? String(vr.defaultRateCents / 100) : "");
      setDefaultTravelFeeInput(vr.defaultTravelFeeCents ? String(vr.defaultTravelFeeCents / 100) : "");
      if (Array.isArray(vr.flatRateOfferings)) setFlatOfferings(vr.flatRateOfferings);
    }
  }, [vendor]);

  const handleSaveRates = async () => {
    setSavingRates(true);
    try {
      const defaultRateCents = defaultRateInput ? Math.round(parseFloat(defaultRateInput) * 100) : null;
      const defaultTravelFeeCents = defaultTravelFeeInput ? Math.round(parseFloat(defaultTravelFeeInput) * 100) : null;
      await apiRequest("PATCH", "/api/vendor-portal/profile", {
        rateType: defaultRateType,
        defaultRateCents,
        defaultTravelFeeCents,
        flatRateOfferings: flatOfferings,
      });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      toast({ title: "Rate settings saved" });
    } catch (e: unknown) {
      toast({ title: "Error", description: parseErrorMessage(e, "Failed to save rate settings"), variant: "destructive" });
    } finally {
      setSavingRates(false);
    }
  };

  const handleSavePersonal = async () => {
    setSavingPersonal(true);
    setPersonalError("");
    try {
      await apiRequest("PATCH", "/api/vendor-portal/profile", {
        firstName: personalFirstName,
        lastName: personalLastName,
      });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      toast({ title: "Contact info updated" });
    } catch (e: unknown) {
      setPersonalError(parseErrorMessage(e, "Failed to update contact info"));
    } finally {
      setSavingPersonal(false);
    }
  };

  const openChangePhone = () => {
    setChangePhoneStep("send");
    setChangePhoneCode("");
    setChangePhoneNewNumber("");
    setChangePhoneError("");
    setChangePhoneOpen(true);
  };

  const openChangeEmail = (context: "personal" | "business") => {
    setChangeEmailContext(context);
    setChangeEmailStep("send");
    setChangeEmailCode("");
    setChangeEmailNewAddress("");
    setChangeEmailError("");
    setChangeEmailOpen(true);
  };

  const handleChangeEmailSendCode = async () => {
    setChangeEmailSending(true);
    setChangeEmailError("");
    try {
      await apiRequest("POST", "/api/vendor-portal/profile/change-email/send-code", {});
      setChangeEmailStep("confirm");
    } catch (e: unknown) {
      setChangeEmailError(parseErrorMessage(e, "Failed to send verification code"));
    } finally {
      setChangeEmailSending(false);
    }
  };

  const handleChangeEmailConfirm = async () => {
    if (!changeEmailCode.trim() || !changeEmailNewAddress.trim()) {
      setChangeEmailError("Please enter both the verification code and your new email address.");
      return;
    }
    setChangeEmailConfirming(true);
    setChangeEmailError("");
    try {
      await apiRequest("POST", "/api/vendor-portal/profile/change-email/confirm", {
        code: changeEmailCode.trim(),
        newEmail: changeEmailNewAddress.trim(),
      });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      setChangeEmailOpen(false);
      toast({ title: "Email address updated", description: "Your new email is now active." });
    } catch (e: unknown) {
      setChangeEmailError(parseErrorMessage(e, "Failed to update email address"));
    } finally {
      setChangeEmailConfirming(false);
    }
  };

  const handleChangePhoneSendCode = async () => {
    setChangePhoneSending(true);
    setChangePhoneError("");
    try {
      await apiRequest("POST", "/api/vendor-portal/profile/change-phone/send-code", {});
      setChangePhoneStep("confirm");
    } catch (e: unknown) {
      setChangePhoneError(parseErrorMessage(e, "Failed to send verification code"));
    } finally {
      setChangePhoneSending(false);
    }
  };

  const handleChangePhoneConfirm = async () => {
    if (!changePhoneCode.trim() || !changePhoneNewNumber.trim()) {
      setChangePhoneError("Please enter both the verification code and your new phone number.");
      return;
    }
    setChangePhoneConfirming(true);
    setChangePhoneError("");
    try {
      await apiRequest("POST", "/api/vendor-portal/profile/change-phone/confirm", {
        code: changePhoneCode.trim(),
        newPhone: changePhoneNewNumber.trim(),
      });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      setChangePhoneOpen(false);
      toast({ title: "Phone number updated", description: "Your new phone number is now active for login." });
    } catch (e: unknown) {
      setChangePhoneError(parseErrorMessage(e, "Failed to update phone number"));
    } finally {
      setChangePhoneConfirming(false);
    }
  };

  const handleSavePrefs = async () => {
    if (!session.vendorTeamMemberId) return;
    setSavingPrefs(true);
    try {
      await apiRequest("PATCH", "/api/vendor-portal/preferences", {
        smsTaskAssigned, smsTaskMessage, smsFormMessage,
        emailTaskAssigned, emailTaskMessage, emailFormMessage,
        emailPayoutStatus,
        notifyInAppFormSent,
      });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      toast({ title: "Notification preferences saved" });
    } catch (e: unknown) {
      toast({ title: "Error", description: parseErrorMessage(e, "Failed to save preferences"), variant: "destructive" });
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await apiRequest("PATCH", "/api/vendor-portal/profile", { name, businessName });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      toast({ title: "Profile updated" });
    } catch (e: unknown) {
      setError(parseErrorMessage(e, "Failed to update profile"));
    } finally {
      setSaving(false);
    }
  };

  // Persist a marketplace photo URL change immediately to the vendor record.
  // Used by upload + remove so vendors don't have to remember to click
  // "Save Marketplace Listing" — uploads were silently dropped if they
  // navigated away, which is why most listed vendors still had no photo
  // (Task #1389).
  const persistMarketplacePhotoUrl = async (newUrl: string | null) => {
    try {
      await apiRequest("PATCH", "/api/vendor-portal/profile", {
        marketplacePhotoUrl: newUrl,
      });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      return true;
    } catch (e: unknown) {
      setMarketplaceError(parseErrorMessage(e, "Failed to save marketplace photo"));
      return false;
    }
  };

  const handleUploadMarketplacePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    setMarketplaceError("");
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const data = reader.result as string;
          const r = await fetch("/api/vendor-portal/marketplace-photo", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ filename: file.name, mimeType: file.type, data }),
          });
          if (!r.ok) {
            const body = await r.json().catch(() => ({}));
            setMarketplaceError(body?.message || "Photo upload failed");
            return;
          }
          const { url } = await r.json();
          const previousUrl = marketplacePhotoUrl;
          setMarketplacePhotoUrl(url);
          const persisted = await persistMarketplacePhotoUrl(url);
          if (persisted) {
            toast({ title: "Marketplace photo updated" });
          } else {
            // Persistence failed — roll the optimistic UI back so the
            // displayed photo matches what's actually on the vendor row.
            setMarketplacePhotoUrl(previousUrl);
          }
        } finally {
          setUploadingPhoto(false);
          if (photoInputRef.current) photoInputRef.current.value = "";
        }
      };
      reader.onerror = () => {
        setMarketplaceError("Failed to read file");
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setMarketplaceError("Photo upload failed");
      setUploadingPhoto(false);
    }
  };

  const handleSaveMarketplace = async () => {
    setSavingMarketplace(true);
    setMarketplaceError("");
    try {
      await apiRequest("PATCH", "/api/vendor-portal/profile", {
        // isMarketplaceListed is no longer accepted by this PATCH — the
        // listing flag is now driven by the Stripe subscription state.
        marketplaceBio,
        marketplacePhotoUrl: marketplacePhotoUrl || null,
      });
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      toast({ title: "Marketplace listing details saved" });
    } catch (e: unknown) {
      setMarketplaceError(parseErrorMessage(e, "Failed to update marketplace listing"));
    } finally {
      setSavingMarketplace(false);
    }
  };

  // Marketplace actions. The server returns a portal URL for Stripe-hosted
  // pages, or `{ ok: true }` for in-place state changes.
  const runMarketplaceAction = async (
    action: "toggle" | "portal" | "pause" | "resume" | "cancel" | "uncancel" | "pro-checkout" | "pro-cancel" | "pro-uncancel" | "pro-downgrade" | "pro-portal",
    successToast?: string,
    body?: Record<string, unknown>,
  ) => {
    setMarketplaceActionPending(action);
    setMarketplaceError("");
    try {
      const res = await apiRequest("POST", `/api/vendor-portal/marketplace-subscription/${action}`, body ?? {});
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url as string;
        return;
      }
      await refetchMarketplaceSub();
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/profile"] });
      if (successToast) toast({ title: successToast });
    } catch (e: unknown) {
      setMarketplaceError(parseErrorMessage(e, "Marketplace subscription action failed"));
    } finally {
      setMarketplaceActionPending(null);
    }
  };

  const handleRemoveMarketplacePhoto = async () => {
    if (uploadingPhoto) return;
    setUploadingPhoto(true);
    setMarketplaceError("");
    const previousUrl = marketplacePhotoUrl;
    try {
      setMarketplacePhotoUrl("");
      const persisted = await persistMarketplacePhotoUrl(null);
      if (persisted) {
        toast({ title: "Marketplace photo removed" });
      } else {
        // Persistence failed — roll the optimistic UI back so the
        // displayed photo matches what's actually on the vendor row.
        setMarketplacePhotoUrl(previousUrl);
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div className="space-y-4">
      {vendorLoading ? (
        <p className="text-sm text-gray-400 text-center py-8">Loading profile...</p>
      ) : (
        <>
          <Card data-tour="profile-security">
            <CardHeader>
              <CardTitle className="text-sm">Security</CardTitle>
              <p className="text-xs text-gray-500">Two-factor authentication adds a 6-digit code on top of your phone-OTP login.</p>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <TotpManagementSection
                basePath="/api/vendor-portal/profile/totp"
                testIdPrefix="vendor"
                disableFlow={{
                  kind: "sms-code",
                  recipientLabel: (vendor as (VendorProfile & { personalPhone?: string | null }) | undefined)?.personalPhone || vendor?.phone || "your phone",
                  sendCode: () => apiRequest("POST", "/api/vendor-portal/profile/totp/send-disable-code", {}),
                }}
              />
            </CardContent>
          </Card>

          <Card data-tour="profile-personal-contact">
            <CardHeader>
              <CardTitle className="text-sm">My Contact Info</CardTitle>
              <p className="text-xs text-gray-500">Update your personal phone, email, and name. Used for OTP login and notifications.</p>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>First Name</Label>
                  <Input value={personalFirstName} onChange={e => setPersonalFirstName(e.target.value)} data-testid="input-personal-first-name" />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input value={personalLastName} onChange={e => setPersonalLastName(e.target.value)} data-testid="input-personal-last-name" />
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 px-3 py-2 text-sm border rounded-md bg-muted text-muted-foreground" data-testid="text-personal-phone">
                    {personalPhone || <span className="italic">Not set</span>}
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={openChangePhone} data-testid="button-change-phone">
                    Change
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">A verification code will be sent to your current number to confirm the change.</p>
              </div>
              <div>
                <Label>Email</Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 px-3 py-2 text-sm border rounded-md bg-muted text-muted-foreground" data-testid="text-personal-email">
                    {personalEmail || <span className="italic">Not set</span>}
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => openChangeEmail("personal")} data-testid="button-change-personal-email">
                    Change
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">A verification code will be sent to your current email to confirm the change.</p>
              </div>
              {personalError && <ErrorBannerWithId message={personalError} />}
              <Button className="w-full" onClick={handleSavePersonal} disabled={savingPersonal} data-testid="button-save-personal">
                {savingPersonal ? "Saving..." : "Save Contact Info"}
              </Button>
            </CardContent>
          </Card>

          {/* Change phone dialog */}
          <Dialog open={changePhoneOpen} onOpenChange={open => { if (!changePhoneSending && !changePhoneConfirming) setChangePhoneOpen(open); }}>
            <DialogContent className="flex flex-col max-h-[90dvh]" onInteractOutside={e => { if (changePhoneSending || changePhoneConfirming) e.preventDefault(); }} onEscapeKeyDown={e => { if (changePhoneSending || changePhoneConfirming) e.preventDefault(); }}>
              <DialogHeader>
                <DialogTitle>Change Phone Number</DialogTitle>
                <DialogDescription>
                  {changePhoneStep === "send"
                    ? `We'll send a 6-digit verification code to your current number (${personalPhone || "on file"}) to confirm it's you.`
                    : "Enter the verification code you received and your new phone number."}
                </DialogDescription>
              </DialogHeader>
              {changePhoneStep === "send" ? (
                <div className="space-y-4 pt-2">
                  {changePhoneError && <ErrorBannerWithId message={changePhoneError} />}
                  <Button className="w-full" onClick={handleChangePhoneSendCode} disabled={changePhoneSending} data-testid="button-change-phone-send-code">
                    {changePhoneSending ? "Sending..." : "Send verification code"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 pt-2">
                  <div>
                    <Label htmlFor="input-change-phone-code">Verification code</Label>
                    <Input
                      id="input-change-phone-code"
                      placeholder="6-digit code"
                      value={changePhoneCode}
                      onChange={e => setChangePhoneCode(e.target.value)}
                      maxLength={6}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      data-testid="input-change-phone-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="input-change-phone-new">New phone number</Label>
                    <Input
                      id="input-change-phone-new"
                      placeholder="(555) 555-5555"
                      value={changePhoneNewNumber}
                      onChange={e => setChangePhoneNewNumber(e.target.value)}
                      inputMode="tel"
                      data-testid="input-change-phone-new"
                    />
                  </div>
                  {changePhoneError && <ErrorBannerWithId message={changePhoneError} />}
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => { setChangePhoneStep("send"); setChangePhoneCode(""); setChangePhoneError(""); }} disabled={changePhoneConfirming} data-testid="button-change-phone-back">
                      Back
                    </Button>
                    <Button className="flex-1" onClick={handleChangePhoneConfirm} disabled={changePhoneConfirming} data-testid="button-change-phone-confirm">
                      {changePhoneConfirming ? "Updating..." : "Update phone number"}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Change email dialog */}
          <Dialog open={changeEmailOpen} onOpenChange={open => { if (!changeEmailSending && !changeEmailConfirming) setChangeEmailOpen(open); }}>
            <DialogContent className="flex flex-col max-h-[90dvh]" onInteractOutside={e => { if (changeEmailSending || changeEmailConfirming) e.preventDefault(); }} onEscapeKeyDown={e => { if (changeEmailSending || changeEmailConfirming) e.preventDefault(); }}>
              <DialogHeader>
                <DialogTitle>Change Email Address</DialogTitle>
                <DialogDescription>
                  {changeEmailStep === "send"
                    ? `We'll send a 6-digit verification code to your current email (${changeEmailContext === "personal" ? (personalEmail || "on file") : (email || "on file")}) to confirm it's you.`
                    : "Enter the verification code you received and your new email address."}
                </DialogDescription>
              </DialogHeader>
              {changeEmailStep === "send" ? (
                <div className="space-y-4 pt-2">
                  {changeEmailError && <ErrorBannerWithId message={changeEmailError} />}
                  <Button className="w-full" onClick={handleChangeEmailSendCode} disabled={changeEmailSending} data-testid="button-change-email-send-code">
                    {changeEmailSending ? "Sending..." : "Send verification code"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 pt-2">
                  <div>
                    <Label htmlFor="input-change-email-code">Verification code</Label>
                    <Input
                      id="input-change-email-code"
                      placeholder="6-digit code"
                      value={changeEmailCode}
                      onChange={e => setChangeEmailCode(e.target.value)}
                      maxLength={6}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      data-testid="input-change-email-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="input-change-email-new">New email address</Label>
                    <Input
                      id="input-change-email-new"
                      placeholder="new@example.com"
                      type="email"
                      value={changeEmailNewAddress}
                      onChange={e => setChangeEmailNewAddress(e.target.value)}
                      data-testid="input-change-email-new"
                    />
                  </div>
                  {changeEmailError && <ErrorBannerWithId message={changeEmailError} />}
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => { setChangeEmailStep("send"); setChangeEmailCode(""); setChangeEmailError(""); }} disabled={changeEmailConfirming} data-testid="button-change-email-back">
                      Back
                    </Button>
                    <Button className="flex-1" onClick={handleChangeEmailConfirm} disabled={changeEmailConfirming} data-testid="button-change-email-confirm">
                      {changeEmailConfirming ? "Updating..." : "Update email address"}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {session.isOwner && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm">Business Details</CardTitle>
                  {vendor?.marketplaceTier === "pro" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-accent px-2 py-0.5 text-xs font-semibold text-white" data-testid={`badge-pro-detail-${vendorId}`}>
                      <Crown className="h-3 w-3" />
                      Pro
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                <div>
                  <Label>Display Name</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} data-testid="input-profile-name" />
                </div>
                <div>
                  <Label>Business Name</Label>
                  <Input value={businessName} onChange={e => setBusinessName(e.target.value)} data-testid="input-profile-business" />
                </div>
                <div>
                  <Label>Business Email</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 px-3 py-2 text-sm border rounded-md bg-muted text-muted-foreground" data-testid="text-profile-email">
                      {email || <span className="italic">Not set</span>}
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={() => openChangeEmail("business")} data-testid="button-change-business-email">
                      Change
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">A verification code will be sent to your current email to confirm the change.</p>
                </div>
                {error && <ErrorBannerWithId message={error} />}
                <Button className="w-full" onClick={handleSave} disabled={saving} data-testid="button-save-profile">
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Task #2187 — Default Rate Settings (owner-only) */}
          {session.isOwner && (
            <Card data-testid="card-rate-settings">
              <CardHeader>
                <CardTitle className="text-sm">Default Rate Settings</CardTitle>
                <p className="text-xs text-gray-500">These defaults pre-fill the estimate dialog when you accept a job. You can always override them per-task.</p>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                <div>
                  <Label className="text-sm font-medium">Default Rate Type</Label>
                  <div className="flex gap-2 mt-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={defaultRateType === "hourly" ? "default" : "outline"}
                      onClick={() => setDefaultRateType("hourly")}
                      data-testid="button-default-rate-type-hourly"
                    >
                      <Clock className="h-3.5 w-3.5 mr-1" />Hourly
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={defaultRateType === "fixed" ? "default" : "outline"}
                      onClick={() => setDefaultRateType("fixed")}
                      data-testid="button-default-rate-type-fixed"
                    >
                      <DollarSign className="h-3.5 w-3.5 mr-1" />Flat / Fixed
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="input-default-rate">
                    {defaultRateType === "hourly" ? "Default Hourly Rate ($)" : "Default Flat Rate ($)"}
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <Input
                      id="input-default-rate"
                      type="number"
                      min="0"
                      step="0.01"
                      className="pl-6"
                      placeholder="0.00"
                      value={defaultRateInput}
                      onChange={e => setDefaultRateInput(e.target.value)}
                      data-testid="input-default-rate"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="input-default-travel-fee">Default Travel Fee ($, optional)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <Input
                      id="input-default-travel-fee"
                      type="number"
                      min="0"
                      step="0.01"
                      className="pl-6"
                      placeholder="0.00"
                      value={defaultTravelFeeInput}
                      onChange={e => setDefaultTravelFeeInput(e.target.value)}
                      data-testid="input-default-travel-fee"
                    />
                  </div>
                </div>
                {/* Flat-rate service offerings */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Named Service Offerings (flat-rate menu)</Label>
                  <p className="text-xs text-gray-500">
                    List specific services with fixed prices so hosts can see exactly what they're getting.
                  </p>
                  {flatOfferings.map((offering, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm" data-testid={`row-flat-offering-${idx}`}>
                      <span className="flex-1 truncate">{offering.name}</span>
                      <span className="text-gray-600 font-medium">${(offering.priceCents / 100).toFixed(2)}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                        onClick={() => setFlatOfferings(prev => prev.filter((_, i) => i !== idx))}
                        data-testid={`button-remove-flat-offering-${idx}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Service name (e.g. Drywall patch)"
                      value={newOfferingName}
                      onChange={e => setNewOfferingName(e.target.value)}
                      className="flex-1 h-8 text-sm"
                      data-testid="input-new-offering-name"
                    />
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={newOfferingPrice}
                        onChange={e => setNewOfferingPrice(e.target.value)}
                        className="pl-5 h-8 text-sm"
                        data-testid="input-new-offering-price"
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8"
                      disabled={!newOfferingName.trim() || !newOfferingPrice}
                      onClick={() => {
                        const priceCents = Math.round(parseFloat(newOfferingPrice) * 100);
                        if (isNaN(priceCents) || priceCents <= 0) return;
                        setFlatOfferings(prev => [...prev, { name: newOfferingName.trim(), priceCents }]);
                        setNewOfferingName("");
                        setNewOfferingPrice("");
                      }}
                      data-testid="button-add-flat-offering"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                <Button className="w-full" onClick={handleSaveRates} disabled={savingRates} data-testid="button-save-rates">
                  {savingRates ? "Saving..." : "Save Rate Settings"}
                </Button>
              </CardContent>
            </Card>
          )}

          {session.isOwner && !session.isMarketplaceBlocked && (
            <Card data-tour="profile-marketplace-listing">
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-sm flex items-center gap-1.5">
                      <Building2 className="h-4 w-4 text-blue-600" />
                      Marketplace listing
                    </CardTitle>
                    <p className="text-xs text-gray-500 mt-1">
                      List yourself in the public PadPulse Vendor Marketplace so hosts in your area can discover and hire you. <strong>Free to list</strong> — first 3 months are always free, then only $19 in months where you accept a marketplace job.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-4">
                {/* Pay-on-job opt-in / opt-out toggle */}
                {(() => {
                  const sub = marketplaceSub;
                  const isOptedIn = !!sub?.marketplaceOptedIn;
                  // Legacy Stripe subscription data (shown only to vendors on the old plan).
                  const legacySub = !!sub?.hasLegacySubscription;
                  const legacyStatus = sub?.subscriptionStatus ?? null;
                  const periodEnd = sub?.currentPeriodEnd ? new Date(sub.currentPeriodEnd) : null;
                  const periodEndLabel = periodEnd
                    ? periodEnd.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                    : null;

                  return (
                    <>
                      {/* New pay-on-job model: simple opt-in toggle */}
                      {!legacySub && (() => {
                        // Compute free-period status from vendor join date (first 3 months free).
                        const createdAt = vendor?.createdAt ? new Date(vendor.createdAt) : null;
                        const freeUntil = createdAt ? new Date(new Date(createdAt).setMonth(createdAt.getMonth() + 3)) : null;
                        const inFreePeriod = freeUntil ? new Date() < freeUntil : false;
                        const freePeriodLabel = freeUntil
                          ? freeUntil.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                          : null;
                        return (
                        <div
                          className={`rounded-md border p-3 space-y-2 ${isOptedIn ? "border-emerald-200 bg-emerald-50" : "border-gray-200 bg-gray-50"}`}
                          data-testid={isOptedIn ? "block-marketplace-opted-in" : "block-marketplace-opted-out"}
                        >
                          {inFreePeriod && (
                            <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                              <span className="text-xs font-semibold text-amber-700">🎉 Free period active</span>
                              <span className="text-xs text-amber-600">— no $19 fee until {freePeriodLabel}</span>
                            </div>
                          )}
                          {isOptedIn ? (
                            <>
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                                <span className="text-sm font-medium text-emerald-900">Listed in the marketplace</span>
                              </div>
                              <p className="text-xs text-emerald-800">
                                Hosts in your area can find and hire you.{inFreePeriod ? " No fees until your free period ends." : " You'll only be charged $19 in months where you accept a job from a new host — $0 otherwise."}
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 border-red-200"
                                onClick={() => runMarketplaceAction("toggle", "Removed from marketplace", { optIn: false })}
                                disabled={marketplaceActionPending === "toggle"}
                                data-testid="button-marketplace-opt-out"
                              >
                                {marketplaceActionPending === "toggle" ? "Saving..." : "Remove from marketplace"}
                              </Button>
                            </>
                          ) : (
                            <>
                              <div className="text-sm font-medium text-gray-700">Not listed in the marketplace</div>
                              <p className="text-xs text-gray-600">
                                Join to let hosts in your area discover and hire you. Only <strong>$19 in months</strong> you accept a marketplace job — otherwise free.
                              </p>
                              <Button
                                size="sm"
                                className="bg-brand hover:bg-brand-hover text-white"
                                onClick={() => runMarketplaceAction("toggle", "Now listed in the marketplace", { optIn: true })}
                                disabled={marketplaceActionPending === "toggle"}
                                data-testid="button-marketplace-opt-in"
                              >
                                {marketplaceActionPending === "toggle" ? "Saving..." : "List me in the marketplace"}
                              </Button>
                            </>
                          )}
                        </div>
                        );
                      })()}

                      {/* ── Pro tier status (ProfileTab) ── */}
                      {(() => {
                        const isProActive = !!marketplaceSub?.isProTier;
                        const proSubStatus = marketplaceSub?.proSubscriptionStatus ?? null;
                        // "active_cancel_pending" = cancel_at_period_end=true; Pro benefits still active.
                        const proIsPendingCancel = isProActive && proSubStatus === "active_cancel_pending";
                        const isOptedIn = !!marketplaceSub?.isMarketplaceListed;

                        if (!isProActive && !isOptedIn) return null;

                        return (
                          <>
                            {isProActive && !proIsPendingCancel && (
                              <div className="rounded-md border border-brand-accent/30 bg-brand-accent-muted p-3 space-y-2" data-testid="block-profile-pro-active">
                                <div className="flex items-center gap-2">
                                  <Crown className="h-4 w-4 text-brand-accent shrink-0" />
                                  <span className="text-sm font-semibold text-brand-accent">Pro — active</span>
                                </div>
                                <p className="text-xs text-gray-700">
                                  Your listing shows a <strong>Pro badge</strong>. The 3% PadPulse fee and $19/month marketplace fee are both waived.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  <Button size="sm" variant="outline" onClick={() => runMarketplaceAction("pro-portal")} disabled={marketplaceActionPending === "pro-portal"} data-testid="button-pro-portal">
                                    {marketplaceActionPending === "pro-portal" ? "Opening..." : "Manage billing"}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => runMarketplaceAction("pro-cancel", "Pro will end at period end")} disabled={marketplaceActionPending === "pro-cancel"} data-testid="button-pro-cancel-profile">
                                    {marketplaceActionPending === "pro-cancel" ? "Cancelling..." : "Cancel Pro"}
                                  </Button>
                                </div>
                              </div>
                            )}

                            {proIsPendingCancel && (
                              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="block-profile-pro-canceling">
                                <div className="flex items-center gap-2">
                                  <Crown className="h-4 w-4 text-brand-accent shrink-0" />
                                  <span className="text-sm font-semibold text-amber-900">Pro — cancellation scheduled</span>
                                </div>
                                <p className="text-xs text-amber-800">Pro will end at the current billing period. Your Pro badge and fee waivers remain active until then.</p>
                                <Button size="sm" variant="outline" onClick={() => runMarketplaceAction("pro-uncancel", "Pro cancellation undone")} disabled={marketplaceActionPending === "pro-uncancel"} data-testid="button-pro-uncancel-profile">
                                  {marketplaceActionPending === "pro-uncancel" ? "Saving..." : "Keep Pro"}
                                </Button>
                              </div>
                            )}

                            {!isProActive && isOptedIn && (
                              <div className="rounded-md border border-brand/20 bg-brand-muted p-3 space-y-2" data-testid="block-profile-pro-upsell">
                                <div className="flex items-center gap-1.5">
                                  <Crown className="h-4 w-4 text-brand shrink-0" />
                                  <span className="text-sm font-semibold text-gray-900">Upgrade to Pro — $99/month</span>
                                </div>
                                <ul className="text-xs text-gray-700 space-y-1">
                                  <li>✓ <strong>Pro badge</strong> on your listing</li>
                                  <li>✓ <strong>No 3% fee</strong> deducted from payouts</li>
                                  <li>✓ <strong>No $19/month</strong> pay-on-job fee</li>
                                </ul>
                                <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" onClick={() => runMarketplaceAction("pro-checkout")} disabled={marketplaceActionPending === "pro-checkout"} data-testid="button-profile-upgrade-pro">
                                  {marketplaceActionPending === "pro-checkout" ? "Opening Stripe..." : "Upgrade to Pro"}
                                </Button>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* Legacy $99/year subscribers: show their sub status + Stripe portal */}
                      {legacySub && (
                        <div
                          className={`rounded-md border p-3 space-y-2 ${
                            legacyStatus === "active" ? "border-emerald-200 bg-emerald-50"
                            : legacyStatus === "paused" ? "border-amber-200 bg-amber-50"
                            : "border-gray-200 bg-gray-50"
                          }`}
                          data-testid="block-marketplace-legacy-sub"
                        >
                          <div className="text-xs text-gray-500 font-medium">Legacy annual subscription</div>
                          {legacyStatus === "active" && (
                            <p className="text-xs text-emerald-800" data-testid="text-marketplace-renewal">
                              {periodEndLabel ? `Your listing renews on ${periodEndLabel}.` : "Active listing."}
                            </p>
                          )}
                          {legacyStatus === "paused" && (
                            <p className="text-xs text-amber-800">Listing paused — hidden from the marketplace.</p>
                          )}
                          {(legacyStatus === "past_due" || legacyStatus === "unpaid") && (
                            <p className="text-xs text-red-800">Payment failed — listing hidden. Update your card to restore it.</p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {legacyStatus === "paused" && (
                              <Button size="sm" onClick={() => runMarketplaceAction("resume", "Listing resumed")} disabled={marketplaceActionPending === "resume"} data-testid="button-marketplace-resume">
                                {marketplaceActionPending === "resume" ? "Resuming..." : "Resume"}
                              </Button>
                            )}
                            {legacyStatus === "active" && (
                              <Button size="sm" variant="outline" onClick={() => runMarketplaceAction("pause", "Listing paused")} disabled={marketplaceActionPending === "pause"} data-testid="button-marketplace-pause">
                                {marketplaceActionPending === "pause" ? "Pausing..." : "Pause listing"}
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => runMarketplaceAction("portal")} disabled={marketplaceActionPending === "portal"} data-testid="button-marketplace-manage">
                              {marketplaceActionPending === "portal" ? "Opening..." : "Manage billing"}
                            </Button>
                            {legacyStatus === "active" && (
                              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => runMarketplaceAction("cancel", "Listing will end at period end")} disabled={marketplaceActionPending === "cancel"} data-testid="button-marketplace-cancel">
                                {marketplaceActionPending === "cancel" ? "Cancelling..." : "Cancel"}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                <div>
                  <Label htmlFor="textarea-marketplace-bio">Bio</Label>
                  <Textarea
                    id="textarea-marketplace-bio"
                    value={marketplaceBio}
                    onChange={e => setMarketplaceBio(e.target.value.slice(0, 600))}
                    placeholder="A short pitch hosts will see — what you do, your service area, what makes you stand out."
                    rows={4}
                    maxLength={600}
                    data-testid="textarea-marketplace-bio"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right" data-testid="text-marketplace-bio-count">
                    {marketplaceBio.length} / 600
                  </p>
                </div>

                <div>
                  <Label>Photo</Label>
                  <div className="flex items-start gap-3 mt-1">
                    {marketplacePhotoUrl ? (
                      <img
                        src={marketplacePhotoUrl}
                        alt="Marketplace photo"
                        className="w-20 h-20 rounded-md object-cover border border-gray-200"
                        data-testid="img-marketplace-photo"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-md border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400 text-xs text-center px-2" data-testid="placeholder-marketplace-photo">
                        No photo
                      </div>
                    )}
                    <div className="flex-1 space-y-2">
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={handleUploadMarketplacePhoto}
                        data-testid="input-marketplace-photo"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        data-testid="button-upload-marketplace-photo"
                      >
                        {uploadingPhoto ? "Uploading..." : marketplacePhotoUrl ? "Change Photo" : "Upload Photo"}
                      </Button>
                      {marketplacePhotoUrl && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={handleRemoveMarketplacePhoto}
                          disabled={uploadingPhoto}
                          data-testid="button-remove-marketplace-photo"
                        >
                          Remove
                        </Button>
                      )}
                      <p className="text-xs text-gray-400">JPG, PNG, WebP, or GIF. Up to 10 MB. Saved automatically when you upload.</p>
                    </div>
                  </div>
                </div>

                {marketplaceError && <ErrorBannerWithId message={marketplaceError} />}
                <Button
                  className="w-full"
                  onClick={handleSaveMarketplace}
                  disabled={savingMarketplace || uploadingPhoto}
                  data-testid="button-save-marketplace"
                >
                  {savingMarketplace ? "Saving..." : "Save Marketplace Listing"}
                </Button>

                {/* Marketplace fee history */}
                <div className="border-t border-gray-100 pt-4" data-testid="section-marketplace-fee-history">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                      <DollarSign className="h-3.5 w-3.5 text-gray-500" />
                      Marketplace Fees
                    </p>
                    {feeHistory.length > 0 && (() => {
                      const feeYears = Array.from(new Set(feeHistory.map(f => f.monthKey.split("-")[0]))).sort((a, b) => b.localeCompare(a));
                      const filteredFees = selectedYear === "all" ? feeHistory : feeHistory.filter(f => f.monthKey.startsWith(selectedYear + "-"));
                      const yearLabel = selectedYear === "all" ? "All Years" : selectedYear;
                      return (
                        <div className="flex items-center gap-1.5">
                          <Select value={selectedYear} onValueChange={setSelectedYear}>
                            <SelectTrigger className="h-6 text-xs px-2 w-auto min-w-[80px]" data-testid="select-fee-year">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All years</SelectItem>
                              {feeYears.map(yr => (
                                <SelectItem key={yr} value={yr}>{yr}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            onClick={() => downloadBulkReceipts(filteredFees, yearLabel)}
                            disabled={exportingBulk || filteredFees.length === 0}
                            className="flex items-center gap-1 text-xs text-brand hover:text-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title={`Export ${yearLabel} receipts as PDF`}
                            data-testid="button-export-all-receipts"
                          >
                            {exportingBulk ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                            Export
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">$19 is charged in any month you accept a marketplace job.</p>
                  {feeHistoryLoading ? (
                    <p className="text-xs text-gray-400 text-center py-3" data-testid="text-fee-history-loading">Loading...</p>
                  ) : feeHistory.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-3" data-testid="text-no-marketplace-fees">No marketplace fees charged yet.</p>
                  ) : (() => {
                    const filteredFees = selectedYear === "all" ? feeHistory : feeHistory.filter(f => f.monthKey.startsWith(selectedYear + "-"));
                    return filteredFees.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-3" data-testid="text-no-fees-for-year">No fees for {selectedYear}.</p>
                    ) : (
                      <div className="divide-y divide-gray-100" data-testid="list-marketplace-fees">
                        {filteredFees.map((fee) => {
                          const [year, month] = fee.monthKey.split("-");
                          const monthLabel = new Date(Number(year), Number(month) - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
                          const dateLabel = new Date(fee.createdAt).toLocaleDateString("default", { month: "short", day: "numeric", year: "numeric" });
                          return (
                            <div key={fee.id} className="py-2 flex items-start justify-between gap-2" data-testid={`fee-row-${fee.id}`}>
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-900" data-testid={`fee-month-${fee.id}`}>{monthLabel}</p>
                                <p className="text-xs text-gray-500" data-testid={`fee-date-${fee.id}`}>Charged {dateLabel}</p>
                                {fee.taskId && fee.taskTitle && (
                                  <p className="text-xs truncate mt-0.5" data-testid={`fee-task-${fee.id}`}>
                                    <a
                                      href={`?tab=tasks&task=${fee.taskId}`}
                                      className="text-brand hover:underline"
                                      data-testid={`link-fee-task-${fee.id}`}
                                    >
                                      Job: {fee.taskTitle}
                                    </a>
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-xs font-semibold text-gray-900" data-testid={`fee-amount-${fee.id}`}>
                                  ${(fee.chargedCents / 100).toFixed(2)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => downloadFeeReceipt(fee)}
                                  className="text-gray-400 hover:text-brand transition-colors"
                                  title="Download receipt"
                                  data-testid={`button-download-receipt-${fee.id}`}
                                >
                                  <Download className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}


          <Card>
            <CardHeader><CardTitle className="text-sm">Account Info</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Role</span>
                <span className="font-medium">{session.isOwner ? "Owner" : "Team Member"}</span>
              </div>
              {session.teamMemberName && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium">{session.teamMemberName}</span>
                </div>
              )}
              {vendor && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Vendor</span>
                  <span className="font-medium">{vendor.name}</span>
                </div>
              )}
              {!!vendor?.serviceTypes && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Services</span>
                  <span className="font-medium text-right">{Array.isArray(vendor.serviceTypes) ? (vendor.serviceTypes as string[]).join(", ") : String(vendor.serviceTypes ?? "")}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {session.vendorTeamMemberId && (
            <Card data-tour="profile-notification-preferences">
              <CardHeader><CardTitle className="text-sm">Notification Preferences</CardTitle></CardHeader>
              <CardContent className="p-4 pt-0 space-y-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">SMS Alerts</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="sms-task-assigned" className="text-sm cursor-pointer">Task assigned to me</Label>
                      <Switch id="sms-task-assigned" checked={smsTaskAssigned} onCheckedChange={setSmsTaskAssigned} data-testid="switch-sms-task-assigned" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="sms-task-message" className="text-sm cursor-pointer">New message on task</Label>
                      <Switch id="sms-task-message" checked={smsTaskMessage} onCheckedChange={setSmsTaskMessage} data-testid="switch-sms-task-message" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="sms-form-message" className="text-sm cursor-pointer">New message on form</Label>
                      <Switch id="sms-form-message" checked={smsFormMessage} onCheckedChange={setSmsFormMessage} data-testid="switch-sms-form-message" />
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Email Alerts</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="email-task-assigned" className="text-sm cursor-pointer">Task assigned to me</Label>
                      <Switch id="email-task-assigned" checked={emailTaskAssigned} onCheckedChange={setEmailTaskAssigned} data-testid="switch-email-task-assigned" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="email-task-message" className="text-sm cursor-pointer">New message on task</Label>
                      <Switch id="email-task-message" checked={emailTaskMessage} onCheckedChange={setEmailTaskMessage} data-testid="switch-email-task-message" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="email-form-message" className="text-sm cursor-pointer">New message on form</Label>
                      <Switch id="email-form-message" checked={emailFormMessage} onCheckedChange={setEmailFormMessage} data-testid="switch-email-form-message" />
                    </div>
                    {session.isOwner && (
                      <div className="flex items-center justify-between">
                        <Label htmlFor="email-payout-status" className="text-sm cursor-pointer">Payout status changes (Stripe paused/approved)</Label>
                        <Switch id="email-payout-status" checked={emailPayoutStatus} onCheckedChange={setEmailPayoutStatus} data-testid="switch-email-payout-status" />
                      </div>
                    )}
                  </div>
                </div>
                {/* Task #2103 — Vendor-wide in-app toggle for the
                    `service_form_sent` notification. Shared across the
                    whole vendor team's bell tray. */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">In-App Alerts</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <Label htmlFor="notify-in-app-form-sent" className="text-sm cursor-pointer">Service form sent</Label>
                        <p className="text-xs text-gray-400">Shows in the bell tray when a host sends or schedules a form for your team.</p>
                      </div>
                      <Switch id="notify-in-app-form-sent" checked={notifyInAppFormSent} onCheckedChange={setNotifyInAppFormSent} data-testid="switch-notify-in-app-form-sent" />
                    </div>
                  </div>
                </div>
                <Button className="w-full" size="sm" onClick={handleSavePrefs} disabled={savingPrefs} data-testid="button-save-preferences">
                  {savingPrefs ? "Saving..." : "Save Preferences"}
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
